'use client';

import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const partnershipTypes = [
{
  title: 'Licensed Users',
  img: 'https://www.nexsys.group/assets/structural-node-B-eQctER.png',
  intro: 'A Licensed User is a company authorised to purchase and use NeXSYS system products to construct buildings or modules within their own factory or directly on site, without reselling the system to third parties.',
  benefits: [
  'Access to fully-certified NeXSYS systems.',
  'Reduced lead times through modular pre-engineering.',
  'Training and certification in system assembly and usage.',
  'Ability to deliver high-quality, code-compliant projects.',
  'Support from NeXSYS technical and project teams.']

},
{
  title: 'Licensed Wholesaler Partner',
  img: 'https://www.nexsys.group/assets/universal-beam-DkTDSBFp.png',
  intro: 'A Licensed Wholesaler Partner purchases NeXSYS systems and distributes them within a defined geographic market, acting as a regional sales and distribution channel under agreed commercial and territorial terms.',
  benefits: [
  'Exclusive regional distribution rights.',
  'Support and training in product specifications and technical guidance.',
  'Enhanced market presence through NeXSYS branding.',
  'Access to marketing and sales resources.',
  'Compliance assurance for resale products.']

},
{
  title: 'Licensed Producer',
  img: 'https://www.nexsys.group/assets/full-bracing-BNV7Z_WA.png',
  intro: 'A Licensed Producer is a modular company or contractor authorised to manufacture NeXSYS systems exclusively for their own projects delivered to clients, without selling the system or components to third parties.',
  benefits: [
  'Access to certified NeXSYS manufacturing processes.',
  'Optimized project timelines and cost savings.',
  'Training in quality assurance and factory production methods.',
  'Ability to deliver ready-to-install modular units.',
  'Technical support from NeXSYS for complex designs.']

},
{
  title: 'Licensed Manufacturers',
  img: 'https://www.nexsys.group/assets/assembly-jig-au7b9Njy.png',
  intro: 'A Licensed Manufacturer is a fully authorised manufacturing partner licensed to produce NeXSYS systems for broader market supply, including third-party projects, within an agreed territory and scope (market TBC).',
  benefits: [
  'Full NeXSYS production licensing and certification.',
  'Training for factory operations, quality control, and system compliance.',
  'Ability to serve multiple clients and markets.',
  'Access to proprietary NeXSYS system innovations.',
  'Competitive advantage as a fully certified system manufacturer.']

}];


const supportItems = [
'Exclusive territory rights',
'Comprehensive training programs',
'Technical engineering support',
'Marketing and sales materials',
'Lead sharing and referrals',
'Co-branded marketing campaigns',
'Priority product access',
'Annual partner summit'];


export default function ChannelPartnersPage() {
  const [formData, setFormData] = useState({ name: '', company: '', email: '', type: 'Select Partnership Type' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async () => {
    if (!formData?.name || !formData?.company || !formData?.email || formData?.type === 'Select Partnership Type') {
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData?.name,
          company: formData?.company,
          email: formData?.email,
          projectType: formData?.type,
          message: `Partner enquiry from Channel Partners page. Partnership Type: ${formData?.type}`,
        }),
      });
      if (res?.ok) {
        setStatus('success');
        setFormData({ name: '', company: '', email: '', type: 'Select Partnership Type' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="pt-24 pb-12 bg-gradient-dark">
          <div className="container mx-auto container-padding">
            <div className="max-w-3xl">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">Channel Partners</span>
              <h1 className="text-hero lg:text-hero-lg font-heading font-bold text-white mt-2 mb-6">
                Partner with <span className="font-bold text-white">NeXSYS</span>
              </h1>
              <p className="text-xl text-white/80 leading-relaxed">
                Join our global network of licensed &amp; Certified Partners transforming construction markets worldwide.
              </p>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="section-padding bg-card">
          <div className="container mx-auto container-padding max-w-4xl">
            <h2 className="text-section lg:text-section-lg font-heading font-bold text-foreground mb-6">
              Use, Sell, or Produce the <span className="text-nexsys-brand">NeXSYS</span> System
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The <span className="font-bold text-nexsys-brand">NeXSYS</span> Platform offers flexible partnership pathways to match your business model, market position, and growth ambitions. Whether you&apos;re a contractor seeking access to certified building systems, a distributor looking to bring innovation to your market, or a manufacturer ready to produce at scale, there&apos;s a clear path for you to join the <span className="font-bold text-nexsys-brand">NeXSYS</span> ecosystem and unlock new opportunities for growth, efficiency, and market leadership.
            </p>
          </div>
        </section>

        {/* Partnership Opportunities */}
        <section className="section-padding">
          <div className="container mx-auto container-padding">
            <div className="mb-12 lg:mb-16 text-center">
              <h2 className="text-section lg:text-section-lg font-heading font-bold text-foreground mb-4">
                Partnership Opportunities
              </h2>
              <p className="text-lg text-muted-foreground">
                Multiple pathways to partner with <span className="font-bold text-nexsys-brand">NeXSYS</span>.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-8">
              {partnershipTypes?.map((type) =>
              <div key={type?.title} className="bg-card rounded-xl overflow-hidden shadow-card border border-border">
                  <div className="p-6">
                    <h3 className="font-heading font-bold text-xl text-foreground mb-3">{type?.title}</h3>
                    <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                      {type?.intro?.split('NeXSYS')?.map((part, i, arr) =>
                    <React.Fragment key={i}>
                          {part}
                          {i < arr?.length - 1 && <span className="font-bold text-nexsys-brand">NeXSYS</span>}
                        </React.Fragment>
                    )}
                    </p>
                    <p className="font-semibold text-foreground text-sm mb-2">Benefits:</p>
                    <ul className="space-y-2">
                      {type?.benefits?.map((b) =>
                    <li key={b} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary flex-shrink-0 mt-0.5">
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                          {b?.split('NeXSYS')?.map((part, i, arr) =>
                      <React.Fragment key={i}>
                              {part}
                              {i < arr?.length - 1 && <span className="font-bold text-nexsys-brand">NeXSYS</span>}
                            </React.Fragment>
                      )}
                        </li>
                    )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Support Section */}
        <section className="section-padding bg-nexsys-surface">
          <div className="container mx-auto container-padding">
            <div className="mb-12 text-center">
              <h2 className="text-section lg:text-section-lg font-heading font-bold text-foreground mb-4">
                Channel Partner Support
              </h2>
              <p className="text-lg text-muted-foreground">We invest in our partners&apos; success.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {supportItems?.map((item) =>
              <div key={item} className="bg-card rounded-xl p-4 shadow-card border border-border flex items-center gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary flex-shrink-0">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  <span className="text-sm font-medium text-foreground">{item}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="section-padding bg-gradient-dark">
          <div className="container mx-auto container-padding">
            <div className="max-w-xl mx-auto">
              <h3 className="text-2xl font-heading font-bold text-white mb-8 text-center">
                Become a Channel Partner
              </h3>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                {status === 'success' ? (
                  <div className="text-center py-6">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary mx-auto mb-4">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                    <p className="text-white text-lg font-semibold">Thank you! We&apos;ll be in touch soon.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Name"
                      value={formData?.name}
                      onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-primary" />
                    <input
                      type="text"
                      placeholder="Company"
                      value={formData?.company}
                      onChange={(e) => setFormData({ ...formData, company: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-primary" />
                    <input
                      type="email"
                      placeholder="Email"
                      value={formData?.email}
                      onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-primary" />
                    <select
                      value={formData?.type}
                      onChange={(e) => setFormData({ ...formData, type: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-primary">
                      <option value="Select Partnership Type">Select Partnership Type</option>
                      <option value="Licensed Users">Licensed Users</option>
                      <option value="Licensed Wholesaler Partner">Licensed Wholesaler Partner</option>
                      <option value="Licensed Producer">Licensed Producer</option>
                      <option value="Licensed Manufacturers">Licensed Manufacturers</option>
                    </select>
                    {status === 'error' && (
                      <p className="text-red-400 text-sm">Please fill in all fields and select a partnership type.</p>
                    )}
                    <button
                      onClick={handleSubmit}
                      disabled={status === 'loading'}
                      className="w-full bg-banana text-banana-foreground font-semibold py-3 rounded-lg hover:bg-banana/90 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60">
                      {status === 'loading' ? 'Sending...' : 'Send'}
                      {status !== 'loading' && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m22 2-7 20-4-9-9-4Z" />
                          <path d="M22 2 11 13" />
                        </svg>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );

}